import React, { useCallback, useEffect, useState } from 'react';
import { createEpicorJob } from '../../services/epicorApi';
import {
  getPricingSummary,
  listPricingProfiles,
  getCustomerPricingContext,
  resolveCustomerPrice,
  getAuditSummary,
  listAuditLines,
} from '../../services/pricingApi';
import { PageHeader, Card, StatGrid, StatCard, Button } from '../ui';

const fmt = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num.toLocaleString() : (n ?? '—');
};
const fmtMoney = (n) => {
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
};
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');
const pct = (a, b) => (b > 0 ? `${((100 * a) / b).toFixed(1)}%` : '—');

// Pipeline stages, in run order. Each enqueues an Epicor job; the worker does the rest.
const STAGES = [
  { id: 'pricing', label: '1. Sync price lists', desc: 'Pull the 9 Epicor pricing tables' },
  { id: 'orders', label: '2. Sync orders', desc: 'Pull recent OrderDtl (audit ground truth)' },
  { id: 'resolve', label: '3. Resolve prices', desc: 'Profiles + resolved prices per part' },
  { id: 'audit', label: '4. Run audit', desc: 'Compare resolved vs actual order prices' },
];

const BUCKET_LABELS = {
  clean: 'Clean',
  manual_override: 'Manual override',
  header_discount: 'Header discount',
  shipto_priced: 'Ship-to priced',
  unmatched_part: 'Unmatched part',
  void: 'Void',
  order_deviation: 'Order deviation (Epicor list price not applied)',
};

// Clean-mismatch classes: engine's resolved discount list vs the list Epicor
// actually used on the order line (OrderDtl.DiscBreakListCode).
const MISMATCH_CLASS_LABELS = {
  no_resolution: 'Engine found no price',
  epicor_no_list: 'Engine has disc list, Epicor applied none',
  engine_no_list: 'Epicor used disc list, engine has none',
  different_list: 'Different discount lists',
  same_list_diff_value: 'Same disc list, different value',
  base_price_only: 'No disc list either side (price differs)',
  '(unclassified)': 'Unclassified (pre-classification audit)',
};

const PricingDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [runMsg, setRunMsg] = useState(null);

  // Inspector state
  const [inspCompany, setInspCompany] = useState('NZ');
  const [inspCust, setInspCust] = useState('');
  const [inspPart, setInspPart] = useState('');
  const [inspQty, setInspQty] = useState('1');
  const [inspLoading, setInspLoading] = useState(false);
  const [inspError, setInspError] = useState(null);
  const [inspContext, setInspContext] = useState(null);
  const [inspResult, setInspResult] = useState(null);

  // Audit browser state
  const [linesCompany, setLinesCompany] = useState('');
  const [linesBucket, setLinesBucket] = useState('clean');
  const [linesMismatchOnly, setLinesMismatchOnly] = useState(true);
  const [lines, setLines] = useState(null);
  const [linesPage, setLinesPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const [s, p, a] = await Promise.all([
        getPricingSummary(),
        listPricingProfiles({ limit: 25 }),
        getAuditSummary(),
      ]);
      setSummary(s);
      setProfiles(p.profiles || []);
      setAuditSummary(a);
    } catch (e) {
      // Non-fatal; panels stay stale.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const loadLines = useCallback(async () => {
    try {
      const params = { page: linesPage, page_size: 25 };
      if (linesCompany) params.company = linesCompany;
      if (linesBucket) params.bucket = linesBucket;
      if (linesMismatchOnly) params.match = false;
      const data = await listAuditLines(params);
      setLines(data);
    } catch (e) {
      setLines(null);
    }
  }, [linesCompany, linesBucket, linesMismatchOnly, linesPage]);

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  const runStage = async (stage) => {
    setRunMsg(null);
    try {
      const { job_id } = await createEpicorJob({ companies: ['AU', 'NZ'], stage });
      setRunMsg({ tone: 'ok', text: `Queued '${stage}' as Epicor job #${job_id}. Track it under Epicor Sync / Job History.` });
    } catch (err) {
      setRunMsg({ tone: 'error', text: err.response?.data?.detail || err.message });
    }
  };

  const inspect = async (e) => {
    e.preventDefault();
    setInspError(null);
    setInspResult(null);
    setInspContext(null);
    if (!inspCust || !inspPart) {
      setInspError('Customer number and part number are required.');
      return;
    }
    setInspLoading(true);
    try {
      const [ctx, res] = await Promise.all([
        getCustomerPricingContext(inspCompany, Number(inspCust)),
        resolveCustomerPrice(inspCompany, Number(inspCust), inspPart.trim(), Number(inspQty) || 1),
      ]);
      setInspContext(ctx);
      setInspResult(res);
    } catch (err) {
      setInspError(err.response?.data?.detail || err.message);
    } finally {
      setInspLoading(false);
    }
  };

  const prov = inspResult?.provenance;

  // Headline stats across companies.
  const totResolved = (summary?.resolved || []).reduce((a, r) => a + Number(r.resolved_prices || 0), 0);
  const totProfiles = (summary?.profiles || []).reduce((a, r) => a + Number(r.profiles || 0), 0);
  const totLists = (summary?.staging || []).reduce((a, r) => a + Number(r.price_lists || 0), 0);
  const cleanBuckets = (auditSummary?.buckets || []).filter((b) => b.audit_bucket === 'clean');
  const cleanLines = cleanBuckets.reduce((a, b) => a + Number(b.lines || 0), 0);
  const cleanMatched = cleanBuckets.reduce((a, b) => a + Number(b.matched || 0), 0);
  const cleanRate = cleanLines > 0 ? `${((100 * cleanMatched) / cleanLines).toFixed(1)}%` : '—';

  return (
    <div className="sync-page pricing-dashboard">
      <PageHeader
        title="Pricing"
        sub="Epicor price-list staging, the price resolution engine (profiles + provenance), and the order-line audit that gates any BigCommerce price push. Nothing here writes to BC."
        actions={(
          <>
            <Button variant="secondary" to="/pricing/price-lists">Price lists</Button>
            <Button variant="secondary" to="/pricing/lookup">Price lookup</Button>
          </>
        )}
      />

      {/* ── Headline stats ── */}
      <StatGrid>
        <StatCard label="Staged price lists" value={fmt(totLists)} />
        <StatCard label="Pricing profiles" value={fmt(totProfiles)} tone="primary" />
        <StatCard label="Resolved prices" value={fmt(totResolved)} tone={totResolved > 0 ? 'success' : undefined} />
        <StatCard
          label="Clean match rate"
          value={cleanRate}
          sub={cleanLines > 0 ? `${fmt(cleanMatched)} of ${fmt(cleanLines)} clean lines` : 'No audit yet'}
          tone={cleanLines > 0 ? (cleanMatched / cleanLines > 0.95 ? 'success' : 'warning') : undefined}
        />
      </StatGrid>

      {/* ── Pipeline runner ── */}
      <Card
        title="Run the pipeline"
        desc="Each step enqueues an Epicor job; the worker does the rest. Run them in order."
      >
        <div className="checkbox-row" style={{ gap: 12, flexWrap: 'wrap' }}>
          {STAGES.map((s) => (
            <Button key={s.id} variant="secondary" onClick={() => runStage(s.id)} title={s.desc}>
              {s.label}
            </Button>
          ))}
        </div>
        {runMsg && (
          <p className={runMsg.tone === 'ok' ? 'muted' : 'error-text'} style={{ marginTop: 8 }}>
            {runMsg.text}
          </p>
        )}
      </Card>

      {/* ── Staging + resolution summary ── */}
      <section className="card">
        <h3 className="section-h">Staging &amp; resolution</h3>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Price lists</th>
              <th>Part entries</th>
              <th>Part breaks</th>
              <th>Group entries</th>
              <th>Group breaks</th>
              <th>Cust disc assigns</th>
              <th>Group disc assigns</th>
              <th>Last synced</th>
            </tr>
          </thead>
          <tbody>
            {!summary?.staging?.length && (
              <tr><td colSpan={9} className="muted">No pricing tables staged yet — run step 1.</td></tr>
            )}
            {(summary?.staging || []).map((r) => (
              <tr key={r.company}>
                <td><strong>{r.company}</strong></td>
                <td>{fmt(r.price_lists)}</td>
                <td>{fmt(r.list_parts)}</td>
                <td>{fmt(r.part_breaks)}</td>
                <td>{fmt(r.list_groups)}</td>
                <td>{fmt(r.group_breaks)}</td>
                <td>{fmt(r.cust_disc_assignments)}</td>
                <td>{fmt(r.group_disc_assignments)}</td>
                <td>{fmtTime(r.last_synced_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Company</th>
              <th>Pricing profiles</th>
              <th>Customers assigned</th>
              <th>Resolved prices</th>
              <th>Last resolved</th>
            </tr>
          </thead>
          <tbody>
            {!summary?.profiles?.length && (
              <tr><td colSpan={5} className="muted">Not resolved yet — run step 3.</td></tr>
            )}
            {(summary?.profiles || []).map((r) => {
              const resolved = (summary?.resolved || []).find((x) => x.company === r.company);
              return (
                <tr key={r.company}>
                  <td><strong>{r.company}</strong></td>
                  <td>{fmt(r.profiles)}</td>
                  <td>{fmt(r.customers)}</td>
                  <td>{fmt(resolved?.resolved_prices)}</td>
                  <td>{fmtTime(r.last_resolved_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* ── Audit summary ── */}
      <section className="card">
        <h3 className="section-h">Order audit</h3>
        <p className="muted">
          Recent order lines bucketed and compared against the engine. Only the <em>clean</em>{' '}
          bucket is a like-for-like test; the other buckets are excluded with a reason.
          The BC push stays blocked until the clean match rate is accepted by an operator.
        </p>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Bucket</th>
              <th>Lines</th>
              <th>Matched</th>
              <th>Mismatched</th>
              <th>Match rate</th>
              <th>Avg |Δ| (mismatch)</th>
            </tr>
          </thead>
          <tbody>
            {!auditSummary?.buckets?.length && (
              <tr><td colSpan={7} className="muted">No audit yet — run steps 2 and 4.</td></tr>
            )}
            {(auditSummary?.buckets || []).map((b) => (
              <tr key={`${b.company}-${b.audit_bucket}`}>
                <td><strong>{b.company}</strong></td>
                <td>{BUCKET_LABELS[b.audit_bucket] || b.audit_bucket || '(unaudited)'}</td>
                <td>{fmt(b.lines)}</td>
                <td>{fmt(b.matched)}</td>
                <td>{fmt(b.mismatched)}</td>
                <td>{b.audit_bucket === 'clean' ? pct(Number(b.matched), Number(b.lines)) : '—'}</td>
                <td>{b.avg_abs_delta != null ? fmtMoney(b.avg_abs_delta) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {auditSummary?.mismatch_classes?.length > 0 && (
          <>
            <h4 style={{ margin: '16px 0 6px' }}>Clean-mismatch classification (discount-list provenance)</h4>
            <table>
              <thead>
                <tr><th>Company</th><th>Class</th><th>Lines</th></tr>
              </thead>
              <tbody>
                {auditSummary.mismatch_classes.map((c) => (
                  <tr key={`${c.company}-${c.mismatch_class}`}>
                    <td>{c.company}</td>
                    <td>{MISMATCH_CLASS_LABELS[c.mismatch_class] || c.mismatch_class}</td>
                    <td>{fmt(c.lines)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {auditSummary?.worst_customers?.length > 0 && (
          <>
            <h4 style={{ margin: '16px 0 6px' }}>Largest mismatch clusters (clean bucket)</h4>
            <table>
              <thead>
                <tr><th>Company</th><th>Customer</th><th>Mismatched lines</th><th>Avg |Δ|</th></tr>
              </thead>
              <tbody>
                {auditSummary.worst_customers.map((w) => (
                  <tr key={`${w.company}-${w.cust_num}`}>
                    <td>{w.company}</td>
                    <td>{w.cust_num}</td>
                    <td>{fmt(w.mismatched_lines)}</td>
                    <td>{fmtMoney(w.avg_abs_delta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* ── Price inspector ── */}
      <section className="card">
        <h3 className="section-h">Price inspector</h3>
        <p className="muted">
          Resolve a live price for one customer + part with full provenance — which list won,
          at which sequence, and how the discount was applied.
        </p>
        <form onSubmit={inspect} className="form-grid">
          <div className="form-group">
            <label className="form-label">Company</label>
            <select value={inspCompany} onChange={(e) => setInspCompany(e.target.value)}>
              <option value="AU">AU</option>
              <option value="NZ">NZ</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Customer #</label>
            <input value={inspCust} onChange={(e) => setInspCust(e.target.value)} placeholder="e.g. 1199" />
          </div>
          <div className="form-group">
            <label className="form-label">Part #</label>
            <input value={inspPart} onChange={(e) => setInspPart(e.target.value)} placeholder="e.g. 22014816" />
          </div>
          <div className="form-group">
            <label className="form-label">Qty</label>
            <input type="number" min="1" value={inspQty} onChange={(e) => setInspQty(e.target.value)} />
          </div>
          <div className="form-group">
            <button type="submit" className="btn" disabled={inspLoading}>
              {inspLoading ? 'Resolving…' : 'Resolve price'}
            </button>
          </div>
        </form>
        {inspError && <p className="error-text">{inspError}</p>}

        {inspResult && (
          <div style={{ marginTop: 12 }}>
            <table>
              <tbody>
                <tr>
                  <th style={{ width: 220 }}>Customer</th>
                  <td>
                    {inspContext?.customer?.name || '—'}{' '}
                    <span className="muted">
                      (group {inspContext?.customer?.group_code || '—'}, profile{' '}
                      {inspContext?.customer?.profile_label || 'unresolved'})
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Resolved price (each)</th>
                  <td><strong>{fmtMoney(inspResult.unit_price_each)}</strong>{' '}
                    <span className="muted">raw basis: {fmtMoney(inspResult.raw_unit_price)}
                      {prov?.price_per_code && prov.price_per_code !== 'E'
                        ? ` (per ${prov.price_per_code === 'C' ? '100' : '1000'})` : ''}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Base price</th>
                  <td>
                    {fmtMoney(prov?.base_price)}{' '}
                    <span className="muted">
                      from {prov?.price_source === 'part_master'
                        ? 'Part master'
                        : `${prov?.price_list_code} (seq ${prov?.price_seq}, ${prov?.price_source})`}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Discount</th>
                  <td>
                    {prov?.discount_percent != null ? `${prov.discount_percent}%` : 'none'}{' '}
                    {prov?.disc_list_code && (
                      <span className="muted">
                        via {prov.disc_list_code} (seq {prov.disc_seq}, {prov.discount_source})
                        {prov.discount_applied ? '' : ' — NOT applied (continuation off)'}
                      </span>
                    )}
                  </td>
                </tr>
                {inspResult.tiers?.length > 1 && (
                  <tr>
                    <th>Quantity tiers</th>
                    <td>
                      {inspResult.tiers.map((t) => (
                        <span key={t.min_qty} className="chip" style={{ marginRight: 8 }}>
                          {t.min_qty}+ → {fmtMoney(t.unit_price)}
                        </span>
                      ))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <h4 style={{ margin: '14px 0 6px' }}>Assignment stack (evaluated top-down)</h4>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {['price_lists', 'disc_lists'].map((kind) => (
                <table key={kind} style={{ maxWidth: 380 }}>
                  <thead>
                    <tr>
                      <th colSpan={3}>{kind === 'price_lists' ? 'Unit-price lists' : 'Discount lists'}</th>
                    </tr>
                    <tr><th>Scope</th><th>Seq</th><th>List</th></tr>
                  </thead>
                  <tbody>
                    {!(inspResult.assignments?.[kind] || []).length && (
                      <tr><td colSpan={3} className="muted">none</td></tr>
                    )}
                    {(inspResult.assignments?.[kind] || []).map((a, i) => {
                      const winning =
                        (kind === 'price_lists' && a.list_code === prov?.price_list_code && a.seq === prov?.price_seq) ||
                        (kind === 'disc_lists' && a.list_code === prov?.disc_list_code && a.seq === prov?.disc_seq);
                      return (
                        <tr key={i} style={winning ? { fontWeight: 700 } : undefined}>
                          <td>{a.scope}</td>
                          <td>{a.seq}</td>
                          <td>{a.list_code}{winning ? ' ◀ winner' : ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Pricing profiles ── */}
      <section className="card">
        <h3 className="section-h">Pricing profiles</h3>
        <p className="muted">
          Customers sharing an identical ordered list stack share one profile — and will share
          one BC price list + customer group. Top 25 by customer count.
        </p>
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Label</th>
              <th>Currency</th>
              <th>Customers</th>
              <th>Resolved parts</th>
              <th>Disc lists (ordered)</th>
            </tr>
          </thead>
          <tbody>
            {!profiles.length && (
              <tr><td colSpan={6} className="muted">No profiles yet — run step 3.</td></tr>
            )}
            {profiles.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.company}</strong></td>
                <td>{p.label}</td>
                <td>{p.currency_code || '—'}</td>
                <td>{fmt(p.customer_count)}</td>
                <td>{fmt(p.resolved_parts)}</td>
                <td className="muted" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(p.disc_lists || []).map((l) => l.list_code).join(' → ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── Audit line browser ── */}
      <section className="card">
        <h3 className="section-h">Audit lines</h3>
        <div className="checkbox-row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <select value={linesCompany} onChange={(e) => { setLinesPage(1); setLinesCompany(e.target.value); }}>
            <option value="">All companies</option>
            <option value="AU">AU</option>
            <option value="NZ">NZ</option>
          </select>
          <select value={linesBucket} onChange={(e) => { setLinesPage(1); setLinesBucket(e.target.value); }}>
            <option value="">All buckets</option>
            {Object.entries(BUCKET_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <label className="chip">
            <input
              type="checkbox"
              checked={linesMismatchOnly}
              onChange={(e) => { setLinesPage(1); setLinesMismatchOnly(e.target.checked); }}
            />
            Mismatches only
          </label>
        </div>
        <table>
          <thead>
            <tr>
              <th>Co</th>
              <th>Order</th>
              <th>Cust</th>
              <th>Part</th>
              <th>Qty</th>
              <th>Actual net</th>
              <th>Resolved</th>
              <th>Δ</th>
              <th>Disc (act / res)</th>
              <th>Winning list</th>
            </tr>
          </thead>
          <tbody>
            {!lines?.lines?.length && (
              <tr><td colSpan={10} className="muted">No lines for this filter.</td></tr>
            )}
            {(lines?.lines || []).map((l, i) => (
              <tr key={i}>
                <td>{l.company}</td>
                <td>{l.order_num}/{l.order_line}</td>
                <td>{l.cust_num}</td>
                <td>{l.part_num}</td>
                <td>{fmt(Number(l.order_qty))}</td>
                <td>{fmtMoney(l.audit_detail?.actual_net ?? l.unit_price)}</td>
                <td>{fmtMoney(l.audit_resolved_price)}</td>
                <td style={{ color: Math.abs(Number(l.audit_price_delta || 0)) > 0.01 ? '#c0392b' : undefined }}>
                  {fmtMoney(l.audit_price_delta)}
                </td>
                <td>{l.discount_percent != null ? `${Number(l.discount_percent)}%` : '—'} / {l.audit_resolved_disc != null ? `${Number(l.audit_resolved_disc)}%` : '—'}</td>
                <td className="muted">{l.audit_detail?.disc_list_code || l.audit_detail?.price_list_code || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {lines && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button type="button" className="btn" disabled={linesPage <= 1} onClick={() => setLinesPage((p) => p - 1)}>
              ‹ Prev
            </button>
            <span className="muted">
              Page {lines.page} — {fmt(lines.total)} lines
            </span>
            <button
              type="button"
              className="btn"
              disabled={lines.page * lines.page_size >= lines.total}
              onClick={() => setLinesPage((p) => p + 1)}
            >
              Next ›
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default PricingDashboard;
